import { callApiV2 } from "@mirage/service-dbx-api";
import type { UnavailableSource } from "@mirage/service-fss-bridge/service/bridge_types";
import { useEffect, useRef, useState } from "react";

import type {
  ChatSourceFssFile,
  ChatSourceFssFolder,
} from "@mirage/mosaics/Chat/types";
import i18n from "@mirage/translations";
import { convertChatSourceToDocumentInfo } from "../utils/convertChatSourceToDocumentInfo";
import { convertDocumentInfoToApiV2ContextEngineDocumentInfo } from "../utils/convertDocumentInfoToApiV2ContextEngineDocumentInfo";

interface UseCheckFileResult {
  unavailableSources: UnavailableSource[];
  isLoading: boolean;
  error: string | null;
}

/**
 * Checks FSS source availability and return unavailable sources.
 * @param sources - The chat sources to check for availability.
 */
export function useUnavailableFssSources(
  sources: (ChatSourceFssFile | ChatSourceFssFolder)[],
): UseCheckFileResult {
  const [unavailableSources, setUnavailableSources] = useState<
    UnavailableSource[]
  >([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const abortControllerRef = useRef<AbortController | null>(null);

  useEffect(() => {
    if (sources.length === 0) {
      setUnavailableSources([]);
      setError(null);
      setIsLoading(false);
      return;
    }

    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    const abortController = new AbortController();
    abortControllerRef.current = abortController;

    setError(null);
    setIsLoading(true);

    const makeApiCall = async () => {
      try {
        const documentInfos = sources.map(convertChatSourceToDocumentInfo);

        const response = await callApiV2(
          "contextEngineConversationsCheckFile",
          {
            document_infos: documentInfos.map(
              convertDocumentInfoToApiV2ContextEngineDocumentInfo,
            ),
          },
        );

        if (abortController.signal.aborted) {
          setIsLoading(false);
          return;
        }

        // Convert CheckFileResult[] to UnavailableSource[]
        const sourcesToCheck = new Map(
          sources.map((source) => [source.fileId, source]),
        );
        const unavailable: UnavailableSource[] = (response.file_checks || [])
          .filter((result) => !result.is_chatable)
          .map((result) => {
            const fileId = result.file_id || "";
            const originalSource = sourcesToCheck.get(fileId);

            if (!originalSource) {
              return null;
            }

            // Create appropriate FSSEntityWithFileId based on source type
            if (originalSource.type === "fss_file") {
              const fssSource = {
                type: "file" as const,
                fileId: originalSource.fileId,
                name: originalSource.name,
                href: originalSource.url || undefined,
              };

              return {
                source: fssSource,
                localizedMessage: getLocalizedErrorMessage(
                  result.error_code || "unknown_error",
                ),
              };
            } else if (originalSource.type === "fss_folder") {
              const fssSource = {
                type: "folder" as const,
                fileId: originalSource.fileId,
                path: originalSource.path,
                name: originalSource.name,
              };

              return {
                source: fssSource,
                localizedMessage: getLocalizedErrorMessage(
                  result.error_code || "unknown_error",
                ),
              };
            }
          })
          .filter((result) => !!result);

        setUnavailableSources(unavailable);
        setIsLoading(false);
      } catch (err) {
        if (!abortController.signal.aborted) {
          setError(
            err instanceof Error ? err.message : "Unknown error occurred",
          );
          setUnavailableSources([]);
          setIsLoading(false);
        }
      }
    };

    makeApiCall();

    return () => {
      abortController.abort();
    };
  }, [sources]);

  return {
    unavailableSources,
    isLoading,
    error,
  };
}

function getLocalizedErrorMessage(errorCode: string): string {
  // Using simple error messages for now since the specific translation keys don't exist yet
  switch (errorCode) {
    case "permission_denied":
      return i18n.t("unavailable_file_permission_denied");
    case "file_not_found":
      return i18n.t("unavailable_file_file_not_found");
    case "file_too_large":
      return i18n.t("unavailable_file_file_too_large");
    default:
      return i18n.t("unavailable_file_unknown_error");
  }
}
